import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRole, useTier } from '@/stores/identity';
import {
  BANNER_SEVERITY_THEMES,
  archiveBanner,
  listOwnerBanners,
  publishBanner,
} from '@/lib/banners';
import type { Banner, BannerStatus } from '@/lib/types';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /owner/banners — Pro/Enterprise feature.
 * Owners compose memos for their own staff. Enterprise tier additionally
 * unlocks acknowledgement-required banners with a per-staff read-receipt
 * report (linked per-row when ack is required).
 */
export default function OwnerBannersList() {
  const role = useRole();
  const tier = useTier();
  const isOwner = role === 'owner';
  const canCompose = tier === 'pro' || tier === 'enterprise';
  const canRequireAck = tier === 'enterprise';

  const [items, setItems] = useState<Banner[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function reload() {
    setStatus('loading');
    try {
      setItems(await listOwnerBanners());
      setStatus('loaded');
    } catch (err) {
      setError(humanize(err));
      setStatus('error');
    }
  }

  useEffect(() => {
    if (!isOwner) return;
    void reload();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="container mx-auto py-8 px-4">
        <p className="text-sm text-muted-foreground">Owner only.</p>
      </div>
    );
  }

  if (!canCompose) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto rounded-lg border border-border bg-card p-6 space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">Communications</h1>
          <p className="text-sm text-muted-foreground">
            Send memos and announcements to your staff with a single click.
            Available on <strong>Pro</strong> and <strong>Enterprise</strong> plans.
          </p>
          <ul className="text-sm space-y-2 text-muted-foreground">
            <li>• Compose styled banners (with image and CTA) shown at the top of every page</li>
            <li>• Filter by role — owners, managers, staff, or any combination</li>
            <li>
              • <strong>Enterprise:</strong> require acknowledgement and see a per-staff
              read-receipt report — meets pharmacy college SOP-dissemination
              documentation requirements
            </li>
          </ul>
          <Link
            to="/billing"
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  async function onPublish(id: string) {
    setBusy(id);
    setError(null);
    try {
      await publishBanner(id);
      await reload();
    } catch (err) {
      setError(humanize(err));
    } finally {
      setBusy(null);
    }
  }

  async function onArchive(id: string) {
    if (!window.confirm('Archive this banner? Staff will stop seeing it immediately.')) return;
    setBusy(id);
    setError(null);
    try {
      await archiveBanner(id);
      await reload();
    } catch (err) {
      setError(humanize(err));
    } finally {
      setBusy(null);
    }
  }

  const activePublished = items.filter((b) => b.status === 'published').length;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Communications</h1>
            <p className="text-sm text-muted-foreground">
              Memos and announcements to your team. {activePublished}/5 active.
              {!canRequireAck && (
                <>
                  {' '}
                  <Link to="/billing" className="underline">Upgrade to Enterprise</Link> for
                  acknowledgement-required banners with read receipts.
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/owner/banners/log"
              className="rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted/40"
            >
              View log
            </Link>
            <Link
              to="/owner/banners/new"
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              + New banner
            </Link>
          </div>
        </div>

        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {status === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No banners yet. Click "+ New banner" to compose your first memo.
          </div>
        )}
        {status === 'loaded' && items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Window</th>
                  <th className="px-3 py-2 text-left">Audience</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => {
                  const theme = BANNER_SEVERITY_THEMES[b.severity];
                  return (
                    <tr key={b.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Link to={`/owner/banners/${b.id}`} className="font-medium hover:underline">
                          {b.title}
                        </Link>
                        {b.acknowledgement_required && (
                          <Link
                            to={`/owner/banners/${b.id}/recipients`}
                            className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-200"
                          >
                            ack · view receipts
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full ${theme.bg} ${theme.text} px-2 py-0.5 text-xs`}>
                          {theme.label}
                        </span>
                      </td>
                      <td className="px-3 py-2"><StatusPill status={b.status} /></td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(b.starts_at)} → {b.ends_at ? formatDate(b.ends_at) : '∞'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {b.target_kind === 'my_org_all' && 'All my staff'}
                        {b.target_kind === 'my_org_roles' && b.target_roles.join(', ')}
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        {b.status === 'draft' && (
                          <button
                            type="button"
                            disabled={busy === b.id}
                            onClick={() => onPublish(b.id)}
                            className="rounded-md bg-green-600 px-2 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
                          >
                            Publish
                          </button>
                        )}
                        {b.status !== 'archived' && (
                          <button
                            type="button"
                            disabled={busy === b.id}
                            onClick={() => onArchive(b.id)}
                            className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40 disabled:opacity-50"
                          >
                            Archive
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: BannerStatus }) {
  const styles: Record<BannerStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    published: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}
